import useAuth from "hooks/useAuth";
import { useTranslation } from "react-i18next";
import { DashboardData } from "services/dashboardService";
import { getCurrencySymbol, toCurrency, toPercent } from "utils/format";
import { cn } from "utils/styles";
import { useState, useRef } from "react";

type Props = {
  variant?: "primary" | "secondary";
  data: DashboardData;
};

const canSeeTheConversionRate = ["66227aa39a54f0f265466127", "668edfe45311c8863f74bd22"];

export const ConvertionCard = ({ variant = "primary", data }: Props) => {
  const { t, i18n } = useTranslation();
  const { selectedProvider } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [modalPosition, setModalPosition] = useState<'above' | 'below'>('above');
  const conversionRateRef = useRef<HTMLParagraphElement>(null);

  const handleMouseEnter = () => {
    if (!canSeeTheConversionRate.includes(selectedProvider.id)) {
      return;
    }

    if (conversionRateRef.current) {
      const rect = conversionRateRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      
      // Se há mais espaço abaixo, posiciona abaixo, senão acima
      setModalPosition(spaceBelow > 200 ? 'below' : 'above');
    }
    setShowModal(true);
  };

  return (
    <div
      className={cn(
        "rounded-2xl flex flex-col items-center justify-center gap-2  min-h-[7rem] laptop:flex-1",
        variant === "primary"
          ? "bg-primaryMultiply/70 px-8 desktop:px-4"
          : "border border-white/30 py-2 px-2 desktop-lg:px-4"
      )}
    >
      <div className="flex items-center gap-2 text-sm">
        <p className={cn("text-secondary", variant === "primary" ? "" : "text-xs")}>
          {t("dashboard.cards.conversionRate")}
        </p>
        <div className="font-input relative">
          <p
            ref={conversionRateRef}
            className={cn(
              "font-bold text-center",
              variant === "primary" ? "text-2xl" : "text-xl",
              canSeeTheConversionRate.includes(selectedProvider.id) ? "cursor-help" : ""
            )}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setShowModal(false)}
          >
            {toPercent(data.byComputed.sells.convertionRate, i18n.language)}
          </p>
          
          {/* Hover Modal */}
          {showModal && (
            <div className={cn(
              "absolute left-1/2 transform -translate-x-1/2 z-50",
              modalPosition === 'above' 
                ? "bottom-full mb-2" 
                : "top-full mt-2"
            )}>
              <div className="bg-primaryMultiply border border-white/30 rounded-lg p-2 shadow-lg w-96">
                <div className="text-xs text-gray-300 space-y-2">
                  <p className="font-medium text-white mb-2">Cálculo da Taxa de Conversão:</p>
                  <p>Oportunidade de venda: {data.byComputed.sellsOpportunity.leads}</p>
                  <p>Vendas no período: {data.byComputed.sells.soldGrouped}</p>
                  <p>Desistências: {data.byStatus.gave_up.leads}</p>
                  <p className="border-t border-gray-600 pt-2 mt-2">
                    <strong>Leads elegíveis: {data.byComputed.sellsOpportunity.leads + data.byComputed.sells.soldGrouped + data.byStatus.gave_up.leads}</strong>
                  </p>
                  <p className="border-t border-gray-600 pt-2 mt-2 space-y-2">
                    <strong>Fórmula: Vendas no período / Leads elegíveis * 100 = Conversão</strong>
                    <p>Cálculo: {data.byComputed.sells.soldGrouped} / {data.byComputed.sellsOpportunity.leads + data.byComputed.sells.soldGrouped + data.byStatus.gave_up.leads} * 100 = {((data.byComputed.sells.soldGrouped / (data.byComputed.sellsOpportunity.leads + data.byComputed.sells.soldGrouped + data.byStatus.gave_up.leads)) * 100).toString().replace('.', ',')}%</p>
                  </p>
                </div>
                {/* Arrow pointing to conversion rate */}
                <div className={cn(
                  "absolute left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent",
                  modalPosition === 'above' 
                    ? "top-full border-t-gray-900" 
                    : "bottom-full border-b-gray-900"
                )}></div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <p className={cn("text-secondary", variant === "primary" ? "" : "text-xs")}>
          {t("dashboard.cards.sellsCost")}
        </p>
        <div className="font-input">
          <p
            className={cn(
              "font-bold text-center",
              variant === "primary" ? "text-2xl" : "text-xl"
            )}
          >
            <span className="text-sm">
              {getCurrencySymbol(selectedProvider.currency, i18n.language)}
            </span>
            {toCurrency(data.byComputed.sells.sellCost, i18n.language, selectedProvider.currency)}
          </p>
        </div>
      </div>
    </div>
  );
};
